/**
 * Unit conversion utilities
 * Refactored from mixins/conversions.js to pure utility functions
 */

const unitList = [
    {
        type: 'length',
        unit: 'centimeters',
        conv: 1
    },
    {
        type: 'length',
        unit: 'inches',
        conv: 2.54
    },
    {
        type: 'weight',
        unit: 'kilograms',
        conv: 1
    },
    {
        type: 'weight',
        unit: 'pounds',
        conv: 1 / 2.2
    },
    {
        type: 'time',
        unit: 'days',
        conv: 1
    },
    {
        type: 'time',
        unit: 'months',
        conv: 30.417
    },
    {
        type: 'time',
        unit: 'years',
        conv: 365
    },
    {
        type: 'time',
        unit: 'minutes',
        conv: 1 / 1440
    },
    {
        type: 'time',
        unit: 'hours',
        conv: 1 / 24
    },
    {
        type: 'temperature',
        unit: 'celsius'
    },
    {
        type: 'temperature',
        unit: 'fahrenheit'
    }
]

/**
 * Convert between units of the same type
 * @param {string} fromUnit - Source unit (e.g., 'celsius', 'kilograms', 'days')
 * @param {string} toUnit - Target unit (e.g., 'fahrenheit', 'pounds', 'months')
 * @param {number} value - Value to convert
 * @param {number} significantFigures - Number of decimal places in result
 * @returns {string|false} Converted value as string with fixed decimals, or false if conversion invalid
 */
export function convert(fromUnit, toUnit, value, significantFigures) {
    let from = unitList.find(x => x.unit === fromUnit)
    let to = unitList.find(x => x.unit === toUnit)
    if (!(from && to) || (from.type !== to.type)) return false
    if (from.unit === 'celsius' && to.unit === 'fahrenheit') {
        return (value * 1.8 + 32).toFixed(significantFigures)
    }
    else if (from.unit === 'fahrenheit' && to.unit === 'celsius') {
        return ((value - 32) / 1.8).toFixed(significantFigures)
    }
    else return (value * from.conv / to.conv).toFixed(significantFigures)
}

/**
 * Convert days to months or years
 * @param {string} toUnits - Target unit ('months' or 'years')
 * @param {number} significantFigures - Number of decimal places
 * @param {number} days - Number of days to convert
 * @returns {string|undefined} Converted value or undefined if toUnits not supported
 */
export function convertFromDays(toUnits, significantFigures, days) {
    if (toUnits === 'months') {
        return (days / 30.417).toFixed(significantFigures)
    }
    else if (toUnits === 'years') {
        return (days / 365).toFixed(significantFigures)
    }
}

/**
 * Convert kilograms to pounds
 * @param {string} toUnits - Target unit (currently only 'pounds' supported)
 * @param {number} significantFigures - Number of decimal places
 * @param {number} kilograms - Weight in kilograms
 * @returns {string|undefined} Converted value or undefined if toUnits not 'pounds'
 */
export function convertFromKilograms(toUnits, significantFigures, kilograms) {
    if (toUnits === 'pounds') {
        return (kilograms * 2.2046).toFixed(significantFigures)
    }
}

/**
 * Convert centimeters to inches
 * @param {string} toUnits - Target unit (currently only 'inches' supported)
 * @param {number} significantFigures - Number of decimal places
 * @param {number} cm - Length in centimeters
 * @returns {string|undefined} Converted value or undefined if toUnits not 'inches'
 */
export function convertFromCm(toUnits, significantFigures, cm) {
    if (toUnits === 'inches') {
        return (cm / 2.54).toFixed(significantFigures)
    }
}

/**
 * Convert Celsius to Fahrenheit with optional multiplier
 * @param {string} toUnits - Target unit (currently only 'fahrenheit' supported)
 * @param {number} significantFigures - Number of decimal places
 * @param {number} multiplier - Multiplier to apply to the constant (32)
 * @param {number} degreesCelsius - Temperature in Celsius
 * @returns {string|undefined} Converted value or undefined if toUnits not 'fahrenheit'
 */
export function convertFromCelsius(toUnits, significantFigures, multiplier, degreesCelsius) {
    if (toUnits === 'fahrenheit') {
        return (degreesCelsius * 1.8 + (32 * multiplier)).toFixed(significantFigures)
    }
}

export function convertAgeToYears(ageInDays) {
    return ageInDays > 365 ? `${ageInDays / 365} years` : `${ageInDays} days`
}
